import { getBinanceServerOffset } from './exchanges/binance.js';
import { deepAnalyzeCandidate, evaluateSupportingCandidate } from './deepCandidateAnalysis.js';
import { archiveCandidates, expireArchivedCandidates } from './storage/candidateArchive.js';
import { isPredictorCandidate, predictorBlockers, shouldQueueForDeepValidation } from '../lib/candidateQualification.js';
import { mergeCandidateWithLiveObservation } from '../lib/candidateLiveMerge.js';
import { selectSupportingCandidates } from '../lib/supportingCandidateSelection.js';
import { rememberSelectedInterval } from './coinAnalysisStore.js';
import { loadPersistedFilters, persistFilters } from './filterPersistence.js';
import { buildRadarSnapshot } from '../lib/predictorSnapshot.js';
import type {
  CandidateRecord,
  DataFreshness,
  PredictorDiagnostics,
  RadarDiagnostics,
  RadarFilters,
  RadarPayload,
  RadarWorkerUpdate,
} from '../types/market.js';

export interface RadarSnapshot {
  predictor: CandidateRecord[];
  monitoring: CandidateRecord[];
  predictorEmptyMessage: string;
  diagnostics: PredictorDiagnostics;
  marketCount: number;
  updatedAt: number;
  freshness: DataFreshness;
  warmupProgress: number;
  connectionLabel: string;
  validationLabel: string;
  error?: string;
}

interface RadarSourceState {
  observations: CandidateRecord[];
  marketCount: number;
  updatedAt: number;
  freshness: DataFreshness;
  warmupProgress: number;
  diagnostics: RadarDiagnostics;
}

const emptyRadarDiagnostics: RadarDiagnostics = {
  eligibleMarkets: 0,
  baselineReadyMarkets: 0,
  directionalObservations: 0,
  neutralObservations: 0,
  externalVenuesAvailable: 0,
  externalVenueIssues: 0,
};

const initialSourceState: RadarSourceState = {
  observations: [],
  marketCount: 0,
  updatedAt: Date.now(),
  freshness: 'delayed',
  warmupProgress: 0,
  diagnostics: emptyRadarDiagnostics,
};

const DEEP_REFRESH_MS = 45_000;
const DEEP_CANDIDATE_LIMIT = 8;
const SUPPORT_REFRESH_MS = 60_000;
const SUPPORT_CANDIDATE_LIMIT = 30;
let supportingValidationRunning = false;
const DEEP_VALIDATION_INITIAL_DELAY_MS = 1_500;
const DEEP_VALIDATION_INTERVAL_MS = 8_000;
const ARCHIVE_INITIAL_DELAY_MS = 2_000;
const ARCHIVE_INTERVAL_MS = 5_000;

/**
 * The radar worker and every piece of predictor state live here, at module scope,
 * so navigating between pages (which unmounts the dashboard) never tears the radar
 * down. The worker is created once, on first subscription, and stays alive for the
 * whole tab session; components subscribe/unsubscribe without stopping it.
 */
let worker: Worker | null = null;
const listeners = new Set<(snapshot: RadarSnapshot) => void>();
let source: RadarSourceState = initialSourceState;
let deepRecords = new Map<string, CandidateRecord>();
let supportingRecords = new Map<string, { analysis: import('../lib/supporting/types').SupportingAnalysis; lastEvaluatedAt: number }>();
let issuedPredictions = new Map<string, CandidateRecord>();
let stablePredictorOrder: string[] = [];
let lastRankReflowAt = 0;
let filters: RadarFilters = loadPersistedFilters();

// The coin-page chart interval FOLLOWS the dashboard horizon (one-way): picking
// an analysis horizon on the dashboard keeps the coin page on that timeframe,
// and on load the remembered chart interval is aligned to the horizon. The
// reverse direction is intentionally NOT synced — changing the chart on a coin
// page is a chart-only exploration and never re-runs the radar's analysis.
rememberSelectedInterval(filters.horizon);
let serverOffsetMs = 0;
let connectionLabel = 'Starting market radar';
let validationLabel = 'Waiting for candidate baseline';
let workerError: string | undefined;
let timeSyncWarning: string | undefined;
let archiveWarning: string | undefined;
const connectionStates = new Map<string, string>();
let validationRunning = false;
let validationGeneration = 0;
let archivedRows: CandidateRecord[] = [];
let lastSnapshot: RadarSnapshot | null = null;
let timersStarted = false;

function surfacedError(): string {
  return [workerError, timeSyncWarning, archiveWarning].filter(Boolean).join(' ');
}
function connectionLabelFromStates(): string {
  const states = [...connectionStates.values()];
  const radarState = connectionStates.get('primary market radar');
  if (radarState === 'stale') return 'Market radar stale';
  if (radarState === 'delayed') return 'Market radar delayed';
  const verified = states.filter((status) => ['verified', 'connected'].includes(status)).length;
  if (verified > 0) return `Verified market data · ${verified} feed${verified === 1 ? '' : 's'}`;
  const waiting = states.filter((status) => ['connecting', 'reconnecting', 'degraded'].includes(status)).length;
  if (waiting > 0) return `Awaiting verified market data · ${waiting} feed${waiting === 1 ? '' : 's'}`;
  return 'Public market feeds unavailable';
}
async function mapWithConcurrency<T, R>(items: T[], concurrency: number, task: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  async function runner(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await task(items[index]!);
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => runner()));
  return results;
}
function validationInputFor(candidate: CandidateRecord, prior?: CandidateRecord): CandidateRecord {
  if (!prior) return candidate;
  const preserveDeepThesis = prior.deepValidated && prior.direction !== 'neutral' && candidate.direction === 'neutral';
  return {
    ...prior,
    ...candidate,
    ...(preserveDeepThesis
      ? {
          direction: prior.direction,
          family: prior.family,
          originalDirection: prior.originalDirection,
          originalFamily: prior.originalFamily,
          foundAt: prior.foundAt,
          expiresAt: prior.expiresAt,
          foundPrice: prior.foundPrice,
          foundChange24h: prior.foundChange24h,
          bestMove: Math.max(prior.bestMove, candidate.bestMove),
          worstMove: Math.min(prior.worstMove, candidate.worstMove),
          transitionReason: prior.transitionReason,
        }
      : {}),
    state: prior.state === 'invalidated' ? 'invalidated' : candidate.state,
    ...(prior.nearestSupport ? { nearestSupport: prior.nearestSupport } : {}),
    ...(prior.nearestResistance ? { nearestResistance: prior.nearestResistance } : {}),
  };
}
function computeSnapshot(): RadarSnapshot {
  const built = buildRadarSnapshot({
    source,
    filters,
    deepRecords,
    supportingRecords,
    state: { issuedPredictions, stablePredictorOrder, lastRankReflowAt },
    serverOffsetMs,
    connectionLabel,
    validationLabel,
    workerError,
    timeSyncWarning,
    archiveWarning,
  });
  issuedPredictions = built.state.issuedPredictions;
  stablePredictorOrder = built.state.stablePredictorOrder;
  lastRankReflowAt = built.state.lastRankReflowAt;
  return built.snapshot;
}

function emit(): void {
  lastSnapshot = computeSnapshot();
  archivedRows = [...lastSnapshot.predictor, ...lastSnapshot.monitoring];
  for (const listener of listeners) listener(lastSnapshot);
}

function handleWorkerUpdate(event: MessageEvent<RadarWorkerUpdate>): void {
  const data = event.data;
  if (data.type === 'RADAR_UPDATE') {
    const payload = data.payload as RadarPayload;
    if (!payload || !Array.isArray(payload.observations) || typeof payload.marketCount !== 'number' || typeof payload.freshness !== 'string') return;
    source = {
      observations: payload.observations,
      marketCount: payload.marketCount,
      updatedAt: payload.updatedAt,
      freshness: payload.freshness,
      warmupProgress: payload.warmupProgress,
      diagnostics: payload.diagnostics,
    };
    if (payload.freshness === 'live') workerError = undefined;
    connectionLabel = payload.freshness === 'live' ? 'Live multi-market radar' : 'Market radar warming up';
  } else if (data.type === 'CONNECTION_STATE') {
    const payload = data.payload as { status: string; source?: string };
    if (!payload || typeof payload.status !== 'string') return;
    if (payload.source) connectionStates.set(payload.source, payload.status);
    connectionLabel = connectionLabelFromStates();
  } else if (data.type === 'ERROR') {
    const payload = data.payload as { message?: string };
    workerError = payload?.message ?? 'Market radar error';
  }
  emit();
}

async function validateDeepCandidates(): Promise<void> {
  if (validationRunning) return;
  const now = Date.now() + serverOffsetMs;
  const candidates = source.observations
    .filter(shouldQueueForDeepValidation)
    .sort((left, right) => right.candidateScore - left.candidateScore || right.movementStrength - left.movementStrength)
    .filter((candidate) => {
      const prior = deepRecords.get(candidate.id);
      return !prior?.lastDeepValidatedAt || now - prior.lastDeepValidatedAt >= DEEP_REFRESH_MS;
    })
    .slice(0, DEEP_CANDIDATE_LIMIT);

  if (candidates.length === 0) {
    const ready = source.diagnostics.baselineReadyMarkets;
    const deepCount = [...deepRecords.values()].filter((record) => record.deepValidated).length;
    validationLabel =
      source.warmupProgress < 100 && ready < 8
        ? `Radar baseline ${source.warmupProgress}% ready`
        : deepCount > 0
          ? `${deepCount} deep record${deepCount === 1 ? '' : 's'} active · waiting for material refresh`
          : 'No observation currently requires deep validation';
    emit();
    return;
  }

  validationRunning = true;
  const generation = validationGeneration;
  validationLabel = `Deep-validating ${candidates.length} observation${candidates.length === 1 ? '' : 's'}`;
  for (const candidate of candidates) {
    const prior = deepRecords.get(candidate.id);
    deepRecords.set(candidate.id, {
      ...(prior ?? candidate),
      validationStatus: 'queued',
      validationIssues: prior?.validationIssues ?? [],
    });
  }
  emit();

  try {
    const results = await mapWithConcurrency(candidates, 2, async (candidate) => {
      const prior = deepRecords.get(candidate.id);
      const validationInput = validationInputFor(candidate, prior);
      try {
        return await deepAnalyzeCandidate(validationInput, now);
      } catch (validationError) {
        return {
          ...validationInput,
          deepValidated: false,
          validationStatus: 'failed' as const,
          validationCoverage: 0,
          validationIssues: [validationError instanceof Error ? validationError.message : 'Deep validation failed unexpectedly.'],
          candidateScore: Math.min(validationInput.candidateScore, 54),
          statusNote: 'Deep validation failed. The observation remains in Eagle Watch and is never silently promoted.',
          lastDeepValidatedAt: now,
        };
      }
    });
    if (generation !== validationGeneration) return;
    for (const result of results) deepRecords.set(result.id, result);
    const activeIds = new Set(source.observations.map((candidate) => candidate.id));
    for (const id of deepRecords.keys()) if (!activeIds.has(id)) deepRecords.delete(id);
    const promoted = results.filter((candidate) => isPredictorCandidate(candidate, filters.minDataQuality)).length;
    const partial = results.filter((candidate) => candidate.validationStatus === 'partial').length;
    const failed = results.filter((candidate) => candidate.validationStatus === 'failed').length;
    validationLabel = `${promoted} promoted · ${partial} partial · ${failed} failed · ${results.length} checked`;
    emit();
  } finally {
    validationRunning = false;
  }
}

async function validateSupportingCandidates(): Promise<void> {
  if (supportingValidationRunning) return;
  const now = Date.now() + serverOffsetMs;
  // Supporting evaluation is independent from the deep-validation queue, but it
  // must consume the same merged Core thesis that the Predictor uses. Raw worker
  // observations deliberately start with deepValidated=false (and are often
  // neutral), so qualifying the raw rows would make this loop permanently empty
  // and, worse, would give supporting methods the wrong direction.
  const candidates = selectSupportingCandidates(
    source.observations,
    deepRecords,
    filters.minDataQuality,
    SUPPORT_CANDIDATE_LIMIT,
  ).filter((candidate) => {
      const prior = supportingRecords.get(candidate.id);
      return !prior || now - prior.lastEvaluatedAt >= SUPPORT_REFRESH_MS;
    });

  if (candidates.length === 0) return;
  supportingValidationRunning = true;
  try {
    await mapWithConcurrency(candidates, 3, async (candidate) => {
      try {
        const analysis = await evaluateSupportingCandidate(candidate, now);
        supportingRecords.set(candidate.id, { analysis, lastEvaluatedAt: now });
      } catch {
        // Supporting evidence is optional. A failed supporting feed never changes
        // the Core candidate or invents a zero/positive result.
      }
    });
    const activeIds = new Set(source.observations.map((candidate) => candidate.id));
    for (const id of supportingRecords.keys()) if (!activeIds.has(id)) supportingRecords.delete(id);
    emit();
  } finally {
    supportingValidationRunning = false;
  }
}

function startBackgroundTimers(): void {
  if (timersStarted) return;
  timersStarted = true;
  window.setTimeout(() => void validateDeepCandidates(), DEEP_VALIDATION_INITIAL_DELAY_MS);
  window.setTimeout(() => void validateSupportingCandidates(), DEEP_VALIDATION_INITIAL_DELAY_MS + 500);
  window.setInterval(() => void validateDeepCandidates(), DEEP_VALIDATION_INTERVAL_MS);
  window.setInterval(() => void validateSupportingCandidates(), DEEP_VALIDATION_INTERVAL_MS);

  const persist = (): void => {
    if (archivedRows.length === 0) return;
    void expireArchivedCandidates()
      .then(() => archiveCandidates(archivedRows))
      .then(() => {
        archiveWarning = undefined;
        emit();
      })
      .catch((reason) => {
        archiveWarning = `Local candidate history could not be saved: ${reason instanceof Error ? reason.message : 'storage unavailable'}. Live analysis continues.`;
        emit();
      });
  };
  window.setTimeout(persist, ARCHIVE_INITIAL_DELAY_MS);
  window.setInterval(persist, ARCHIVE_INTERVAL_MS);
}

function ensureWorker(): void {
  if (worker) return;
  if (typeof Worker === 'undefined') {
    connectionLabel = 'Web Workers unavailable · live radar cannot start';
    emit();
    return;
  }
  const instance = new Worker(new URL('../workers/marketRadar.worker.ts', import.meta.url), { type: 'module' });
  worker = instance;
  instance.onmessage = handleWorkerUpdate;
  instance.postMessage({ type: 'START' });
  instance.postMessage({ type: 'SET_FILTERS', payload: filters });
  void getBinanceServerOffset()
    .then((offset) => {
      serverOffsetMs = offset;
      timeSyncWarning = undefined;
      instance.postMessage({ type: 'SET_SERVER_OFFSET', payload: offset });
      emit();
    })
    .catch(() => {
      serverOffsetMs = 0;
      timeSyncWarning = 'Exchange-time synchronization is unavailable. Candle timing temporarily uses the device clock.';
      instance.postMessage({ type: 'SET_SERVER_OFFSET', payload: 0 });
      emit();
    });
  startBackgroundTimers();
}

/** Subscribe to radar snapshots. Never tears the worker down on unsubscribe. */
export function subscribe(listener: (snapshot: RadarSnapshot) => void): () => void {
  ensureWorker();
  listeners.add(listener);
  if (lastSnapshot) listener(lastSnapshot);
  return () => {
    listeners.delete(listener);
  };
}

/** Return the latest snapshot, computing a placeholder one if none exists yet. */
export function getSnapshot(): RadarSnapshot {
  if (!lastSnapshot) lastSnapshot = computeSnapshot();
  return lastSnapshot;
}

/** Return the filters currently held by the store (survive navigation). */
export function getFilters(): RadarFilters {
  return filters;
}

/** Update filters (deduplicated) and re-evaluate the derived view immediately. */
export function setFilters(next: RadarFilters): void {
  const previous = filters;
  filters = { ...previous, ...next };
  const horizonChanged = filters.horizon !== previous.horizon;
  const changed =
    horizonChanged ||
    filters.direction !== previous.direction ||
    filters.market !== previous.market ||
    filters.search !== previous.search ||
    filters.minQuoteVolume !== previous.minQuoteVolume ||
    filters.minDataQuality !== previous.minDataQuality;
  if (horizonChanged) rememberSelectedInterval(filters.horizon);
  if (changed) {
    validationGeneration += 1;
    validationLabel = 'Re-evaluating candidates for selected filters';
    worker?.postMessage({ type: 'SET_FILTERS', payload: filters });
  }
  persistFilters(filters);
  emit();
}
